import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, apiFetchNoEncode } from '../Utils/httpClient';
import { reportSearchError } from '../Utils/reportSearchError';
import { useAppDispatch } from '../Store/types';
import type { paths } from '../Types/api';
import { filterProducts, mergeProductResults, type ProductSearchHit } from '../Utils/productSearchUtils';

type OffersResponse = paths['/api/Order/Offers']['get']['responses'][200]['content']['application/json'];

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const hasValue = (value?: string | null): value is string => (value ?? '') !== '';

export const useProductSearch = (customerNumber?: string | null, customerId?: string | null) => {
    const dispatch = useAppDispatch();
    const [results, setResults] = useState<ProductSearchHit[]>([]);
    const offersRef = useRef<Promise<ProductSearchHit[]> | null>(null);
    const offersAbortControllerRef = useRef<AbortController | null>(null);
    const searchAbortControllerRef = useRef<AbortController | null>(null);
    const lastQueryRef = useRef('');

    const reportError = useCallback(
        (error: unknown, signal: AbortSignal) => {
            if (!signal.aborted) {
                void reportSearchError(dispatch, error);
            }
        },
        [dispatch]
    );

    const loadOffers = useCallback((): Promise<ProductSearchHit[]> => {
        offersAbortControllerRef.current?.abort();
        offersAbortControllerRef.current = null;

        if (!hasValue(customerId) || customerId === EMPTY_GUID) {
            offersRef.current = Promise.resolve([]);
            return offersRef.current;
        }

        const controller = new AbortController();
        offersAbortControllerRef.current = controller;

        const offers: Promise<ProductSearchHit[]> = apiFetch<OffersResponse>(`/api/Order/Offers?customerId=${customerId}`, { signal: controller.signal })
            .then((data) => data ?? [])
            .catch((error: unknown) => {
                reportError(error, controller.signal);
                if (offersRef.current === offers) {
                    offersRef.current = null;
                }
                return [];
            });

        offersRef.current = offers;
        return offers;
    }, [customerId, reportError]);

    useEffect(() => {
        void loadOffers();
        return () => offersAbortControllerRef.current?.abort();
    }, [loadOffers]);

    const searchProducts = useCallback(
        async (query: string, signal: AbortSignal): Promise<ProductSearchHit[]> => {
            const customerParam = hasValue(customerNumber) ? `&customerNumber=${encodeURIComponent(customerNumber)}` : '';
            try {
                return (await apiFetchNoEncode<ProductSearchHit[]>(`/api/Order/Search?input=${encodeURIComponent(query)}${customerParam}`, { signal })) ?? [];
            } catch (error) {
                reportError(error, signal);
                return [];
            }
        },
        [customerNumber, reportError]
    );

    const search = useCallback(
        async (query: string): Promise<void> => {
            lastQueryRef.current = query;
            searchAbortControllerRef.current?.abort();
            searchAbortControllerRef.current = null;

            if (!query.trim()) {
                setResults([]);
                return;
            }

            const controller = new AbortController();
            searchAbortControllerRef.current = controller;

            const [offers, products] = await Promise.all([offersRef.current ?? loadOffers(), searchProducts(query, controller.signal)]);

            if (!controller.signal.aborted) {
                setResults(mergeProductResults(filterProducts(offers, query), products));
            }
        },
        [loadOffers, searchProducts]
    );

    useEffect(() => {
        if (lastQueryRef.current.trim()) {
            void search(lastQueryRef.current);
        }
    }, [search]);

    useEffect(() => () => searchAbortControllerRef.current?.abort(), []);

    return { search, results };
};
